import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle, ChevronRight, ChevronLeft, FileText, Loader2,
  AlertCircle, Shield, PenLine, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Engagements Attestation sur l'honneur ─────────────────────────────────────
const ATTESTATION_ENGAGEMENTS = [
  "Je déclare ne pas être accrédité(e) chez un autre représentant ou fournisseur incompatible avec les missions qui me sont confiées et m'engage à informer immédiatement l'employeur de toute évolution de cette situation.",
  "Je m'engage à commercialiser uniquement les produits et services expressément autorisés par la société dans le cadre de mes missions.",
  "Je m'engage à ne pas utiliser ou véhiculer, dans le cadre de mes missions, l'image d'une entreprise ou d'un produit non autorisé par l'employeur ou le partenaire concerné.",
  "Je m'engage à respecter la ou les chartes éthiques, procédures de vente, règles qualité et consignes de conformité qui m'ont été remises.",
  "Je certifie l'exactitude des informations déclarées dans le cadre de mon intégration et reconnais que toute fausse déclaration ou manquement pourra entraîner les conséquences prévues par les règles applicables.",
];

const MOTIFS_DISPENSE = [
  { value: 'presence_regime', label: "Présence dans l'entreprise lors de la mise en place du régime (refus initial)" },
  { value: 'cdd_3_mois', label: 'CDD ≤ 3 mois avec couverture individuelle conforme (justificatif requis)' },
  { value: 'css', label: 'Bénéficiaire de la Complémentaire Santé Solidaire (CSS)', needsDate: true },
  { value: 'assurance_individuelle', label: 'Couverture par une assurance individuelle frais de santé en cours', needsOrg: true, needsDate: true },
  { value: 'couverture_collective', label: "Ayant droit d'une couverture collective obligatoire (autre régime)", needsOrg: true },
  { value: 'temps_partiel', label: "Salarié à temps très partiel (cotisation > 10% du salaire brut)" },
  { value: 'cdd_apprentissage', label: 'CDD, apprentissage ou mission — cas de dispense prévu par le régime' },
];

// ── PDF Viewer ─────────────────────────────────────────────────────────────────
function PdfViewer({ url, b64, label }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const prevBlobRef = useRef(null);

  useEffect(() => {
    setBlobUrl(null);
    setFetchError(false);

    if (b64) {
      setFetchLoading(true);
      try {
        const blob = new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const newUrl = URL.createObjectURL(blob);
        if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
        prevBlobRef.current = newUrl;
        setBlobUrl(newUrl);
      } catch {
        setFetchError(true);
      } finally {
        setFetchLoading(false);
      }
      return;
    }

    if (!url) return;
    setFetchLoading(true);
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const newUrl = URL.createObjectURL(blob);
        if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
        prevBlobRef.current = newUrl;
        setBlobUrl(newUrl);
        setFetchError(false);
      })
      .catch(() => setFetchError(true))
      .finally(() => setFetchLoading(false));

    return () => {
      if (prevBlobRef.current) { URL.revokeObjectURL(prevBlobRef.current); prevBlobRef.current = null; }
    };
  }, [url, b64]);

  if (fetchLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] bg-slate-50 rounded-xl border border-slate-200">
        <Loader2 className="animate-spin text-blue-500 mb-3" size={32} />
        <p className="text-sm text-slate-500">Génération du document en cours…</p>
        <p className="text-xs text-slate-400 mt-1">Quelques secondes</p>
      </div>
    );
  }
  if (fetchError || !blobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] bg-red-50 rounded-xl border border-red-200">
        <AlertCircle className="text-red-400 mb-2" size={28} />
        <p className="text-sm text-red-500">Impossible de charger le document</p>
        <p className="text-xs text-red-400 mt-1">Vérifiez votre connexion et réessayez</p>
      </div>
    );
  }
  return (
    <iframe
      src={blobUrl}
      title={label}
      className="w-full rounded-xl border border-slate-200 shadow-sm"
      style={{ height: '65vh', minHeight: 400 }}
    />
  );
}

// ── Custom Radio ───────────────────────────────────────────────────────────────
function RadioOption({ name, value, checked, onChange, label, desc, color = 'blue' }) {
  const colors = {
    blue: { ring: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd', dot: '#1d4ed8' },
    teal: { ring: '#0d9488', bg: '#f0fdfa', border: '#5eead4', dot: '#0d9488' },
  };
  const c = colors[color] || colors.blue;
  return (
    <label
      onClick={onChange}
      className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
      style={{
        border: `1px solid ${checked ? c.border : '#e2e8f0'}`,
        background: checked ? c.bg : '#fff',
      }}
    >
      {/* Radio visuel personnalisé */}
      <span style={{
        width: 18, height: 18, borderRadius: '50%', border: `2px solid ${checked ? c.dot : '#94a3b8'}`,
        background: checked ? c.dot : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 2, transition: 'all 0.15s',
      }}>
        {checked && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff', display: 'block' }} />}
      </span>
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
      </div>
    </label>
  );
}

// ── Custom Checkbox ─────────────────────────────────────────────────────────────
function CheckOption({ checked, onChange, label, color = 'indigo' }) {
  const colors = {
    indigo: { bg: '#4f46e5', border: '#6366f1', light: '#eef2ff' },
    blue:   { bg: '#1d4ed8', border: '#3b82f6', light: '#eff6ff' },
  };
  const c = colors[color] || colors.indigo;
  return (
    <label
      onClick={onChange}
      className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
      style={{
        border: `1px solid ${checked ? c.border : '#e2e8f0'}`,
        background: checked ? c.light : '#fff',
      }}
    >
      {/* Checkbox visuelle personnalisée */}
      <span style={{
        width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? c.bg : '#94a3b8'}`,
        background: checked ? c.bg : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 2, transition: 'all 0.15s',
      }}>
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
      <span className="text-xs text-gray-700 leading-relaxed">{label}</span>
    </label>
  );
}
function DfsChoiceForm({ choices, onChange, villeSignature, onVilleChange }) {
  return (
    <div className="space-y-4 p-5 bg-blue-50 border border-blue-200 rounded-xl">
      <div className="flex items-center gap-2">
        <PenLine size={16} className="text-blue-600" />
        <h3 className="font-semibold text-blue-800 text-sm">Déduction Forfaitaire Spécifique (DFS)</h3>
      </div>
      <p className="text-xs text-blue-700">
        La DFS est une déduction de <strong>30%</strong> appliquée sur votre revenu imposable liée aux frais professionnels.
        Elle peut impacter votre revenu fiscal de référence.
      </p>
      <div className="space-y-2">
        <RadioOption value="accepte" checked={choices.dfs_choix === 'accepte'}
          onChange={() => onChange({ ...choices, dfs_choix: 'accepte' })}
          label="J'accepte l'application de la DFS (déduction de 30%)" desc="Mon revenu fiscal sera réduit" color="blue" />
        <RadioOption value="refuse" checked={choices.dfs_choix === 'refuse'}
          onChange={() => onChange({ ...choices, dfs_choix: 'refuse' })}
          label="Je refuse l'application de la DFS" desc="Mes revenus seront déclarés sans déduction" color="blue" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Lieu de signature</label>
        <input type="text" value={villeSignature} onChange={e => onVilleChange(e.target.value)} placeholder="Ex : Nantes"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
      </div>
    </div>
  );
}

// ── Mutuelle Choice Form ───────────────────────────────────────────────────────
function MutuelleChoiceForm({ choices, onChange, villeSignature, onVilleChange }) {
  const motif = MOTIFS_DISPENSE.find(m => m.value === choices.mutuelle_motif);
  return (
    <div className="space-y-4 p-5 bg-teal-50 border border-teal-200 rounded-xl">
      <div className="flex items-center gap-2">
        <Shield size={16} className="text-teal-600" />
        <h3 className="font-semibold text-teal-800 text-sm">Mutuelle Santé — Affiliation / Dispense</h3>
      </div>
      <div className="space-y-2">
        <RadioOption value="affiliation" checked={choices.mutuelle_choix === 'affiliation'}
          onChange={() => onChange({ ...choices, mutuelle_choix: 'affiliation', mutuelle_motif: '' })}
          label="J'adhère à la mutuelle de l'entreprise" desc="Cotisation déduite de la paie" color="teal" />
        <RadioOption value="dispense" checked={choices.mutuelle_choix === 'dispense'}
          onChange={() => onChange({ ...choices, mutuelle_choix: 'dispense' })}
          label="Je demande une dispense d'adhésion" desc="Je dois justifier d'un motif valable" color="teal" />
      </div>

      {choices.mutuelle_choix === 'dispense' && (
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-gray-700">Motif de dispense *</label>
          <select value={choices.mutuelle_motif || ''} onChange={e => onChange({ ...choices, mutuelle_motif: e.target.value })}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none bg-white">
            <option value="">— Sélectionnez un motif —</option>
            {MOTIFS_DISPENSE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          {motif?.needsDate && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">{motif.value === 'css' ? 'Date de fin de CSS' : "Date de fin de l'assurance"} *</label>
              <input type="text" placeholder="JJ/MM/AAAA"
                value={choices.mutuelle_date_fin_css || choices.mutuelle_date_fin_assurance || ''}
                onChange={e => { const f = motif.value === 'css' ? 'mutuelle_date_fin_css' : 'mutuelle_date_fin_assurance'; onChange({ ...choices, [f]: e.target.value }); }}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
          )}
          {motif?.needsOrg && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nom de l'organisme *</label>
              <input type="text" placeholder={motif.value === 'assurance_individuelle' ? "Ex : AXA" : "Ex : Malakoff Médéric"}
                value={choices.mutuelle_organisme_assurance || choices.mutuelle_organisme_collectif || ''}
                onChange={e => { const f = motif.value === 'assurance_individuelle' ? 'mutuelle_organisme_assurance' : 'mutuelle_organisme_collectif'; onChange({ ...choices, [f]: e.target.value }); }}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
          )}
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Lieu de signature</label>
        <input type="text" value={villeSignature} onChange={e => onVilleChange(e.target.value)} placeholder="Ex : Nantes"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none" />
      </div>
    </div>
  );
}

// ── Attestation Step ───────────────────────────────────────────────────────────
function AttestationStep({ checks, onCheck, villeSignature, onVilleChange }) {
  const allChecked = checks.every(Boolean);
  return (
    <div className="space-y-3 p-5 bg-indigo-50 border border-indigo-200 rounded-xl">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle size={16} className="text-indigo-600" />
        <h3 className="font-semibold text-indigo-800 text-sm">Attestation sur l'honneur — Engagements</h3>
      </div>
      <p className="text-xs text-indigo-700">Veuillez lire et cocher chacun des engagements ci-dessous :</p>
      <div className="space-y-2">
        {ATTESTATION_ENGAGEMENTS.map((eng, i) => (
          <CheckOption key={i} checked={checks[i]} onChange={() => onCheck(i)} label={eng} color="indigo" />
        ))}
      </div>
      {allChecked && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Lieu de signature</label>
          <input type="text" value={villeSignature} onChange={e => onVilleChange(e.target.value)} placeholder="Ex : Nantes"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" />
        </div>
      )}
    </div>
  );
}

// ── Step Indicator ─────────────────────────────────────────────────────────────
function StepIndicator({ steps, current }) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex items-center gap-1 min-w-max">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div key={i} className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                active ? 'bg-blue-600 text-white shadow-sm' : done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
              }`}>
                {done ? <CheckCircle size={11} /> : <span className="w-4 h-4 rounded-full flex items-center justify-center bg-current/20 text-[10px]">{i + 1}</span>}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`h-px w-3 flex-shrink-0 ${i < current ? 'bg-emerald-300' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function SigningFlow() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingInfo, setSigningInfo] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);

  // PDF courant (pour les étapes "pdf")
  const [pdfUrl, setPdfUrl] = useState(null);

  // Preview PDF (pour les étapes "choice" et "attestation")
  const [previewB64, setPreviewB64] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);

  // Choix accumulés
  const [dfsChoices, setDfsChoices] = useState({ dfs_choix: 'accepte' });
  const [mutuelleChoices, setMutuelleChoices] = useState({ mutuelle_choix: 'affiliation' });
  const [attestationChecks, setAttestationChecks] = useState([false, false, false, false, false]);
  const [villeSignature, setVilleSignature] = useState('');

  // ── Chargement des infos de signature ───────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/signing/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) { setError(data.detail); setLoading(false); return; }
        setSigningInfo(data);
        setVilleSignature(data.employee?.ville || '');
        if (data.already_signed) {
          setCompleted(true);
        }
        setLoading(false);
      })
      .catch(() => { setError('Impossible de charger les informations de signature.'); setLoading(false); });
  }, [token]);

  // ── Chargement du PDF pour les étapes "pdf" ──────────────────────────────────
  useEffect(() => {
    if (!signingInfo) return;
    const steps = signingInfo.document_steps || [];
    const step = steps[currentStep];
    if (!step || step.type !== 'pdf') {
      setPdfUrl(null);
      return;
    }

    let url = '';
    if (step.doc === 'contract') {
      url = `${API}/api/signing/${token}/pdf/contract`;
    } else if (step.doc.startsWith('annexe_')) {
      const partner = step.doc.replace('annexe_', '');
      url = `${API}/api/signing/${token}/pdf/annexe/${partner}`;
    } else if (step.doc === 'notice_dsn') {
      url = `${API}/api/signing/${token}/pdf/vrp-doc/notice_dsn`;
    }
    setPdfUrl(url);
    setAcknowledged(false);
    setPreviewB64(null);
    setPreviewGenerated(false);
  }, [currentStep, signingInfo, token]);

  // ── Réinitialiser l'état quand on change d'étape ─────────────────────────────
  useEffect(() => {
    setAcknowledged(false);
    setPreviewB64(null);
    setPreviewGenerated(false);
  }, [currentStep]);

  // ── Prévisualiser un document avec les choix ─────────────────────────────────
  const handlePreview = async (docType, choices) => {
    setPreviewLoading(true);
    setPreviewB64(null);
    try {
      const res = await fetch(`${API}/api/signing/${token}/preview-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_type: docType, choices: { ...choices, ville_signature: villeSignature } }),
      });
      const data = await res.json();
      if (data.pdf_b64) {
        setPreviewB64(data.pdf_b64);
        setPreviewGenerated(true);
      } else {
        alert('Erreur lors de la génération du document. Vérifiez vos informations.');
      }
    } catch {
      alert('Erreur réseau. Veuillez réessayer.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Finaliser la signature ────────────────────────────────────────────────────
  const handleComplete = async () => {
    setCompleting(true);
    try {
      const esignature_choices = {
        ...dfsChoices,
        ...mutuelleChoices,
        attestation_all_checked: attestationChecks.every(Boolean),
        ville_signature: villeSignature,
      };

      const res = await fetch(`${API}/api/signing/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ esignature_choices }),
      });
      const data = await res.json();
      if (data.success) {
        setCompleted(true);
      } else {
        alert(data.detail || 'Erreur lors de la finalisation.');
      }
    } catch {
      alert('Erreur réseau. Veuillez réessayer.');
    } finally {
      setCompleting(false);
    }
  };

  const handleNext = () => {
    const steps = signingInfo?.document_steps || [];
    const isLast = currentStep === steps.length - 1;
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep(s => s + 1);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="force-light min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-500 mx-auto mb-3" size={36} />
          <p className="text-slate-500 text-sm">Chargement…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="force-light min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="text-red-400 mx-auto mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Lien invalide</h1>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="force-light min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-600" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">Signature complète !</h1>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            Merci {signingInfo?.employee?.prenom || ''} ! Tous vos documents ont été lus et signés.
            Notre équipe a été notifiée et vous recevrez une confirmation par email.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left">
            <p className="text-xs font-semibold text-emerald-700 mb-2">Documents traités :</p>
            <ul className="space-y-1">
              {(signingInfo?.document_steps || []).map((s, i) => (
                <li key={i} className="flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle size={11} /> {s.label}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            Vous pouvez fermer cette page.
          </p>
        </div>
      </div>
    );
  }

  if (!signingInfo) return null;

  const steps = signingInfo.document_steps || [];
  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const employee = signingInfo.employee || {};
  const allAttestationChecked = attestationChecks.every(Boolean);

  // Déterminer si le bouton "Suivant" est activé
  const canProceed = () => {
    if (!step) return false;
    if (step.type === 'pdf') return acknowledged;
    if (step.type === 'attestation') return allAttestationChecked && previewGenerated && acknowledged;
    if (step.type === 'choice_dfs') return previewGenerated && acknowledged;
    if (step.type === 'choice_mutuelle') {
      if (!previewGenerated || !acknowledged) return false;
      if (mutuelleChoices.mutuelle_choix === 'dispense' && !mutuelleChoices.mutuelle_motif) return false;
      return true;
    }
    return false;
  };

  return (
    <div className="force-light min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-bold text-lg">O Prime Energy</h1>
            <p className="text-xs text-blue-200">Signature électronique</p>
          </div>
          <div className="text-right">
            <p className="font-medium text-sm">{employee.prenom} {(employee.nom || '').toUpperCase()}</p>
            <p className="text-xs text-blue-300">{signingInfo.contract?.type_contrat}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step indicator */}
        <div className="mb-6">
          <StepIndicator steps={steps} current={currentStep} />
        </div>

        {/* Current step label */}
        <div className="flex items-center gap-2 mb-4">
          <FileText size={16} className="text-blue-600" />
          <h2 className="font-semibold text-gray-800 text-lg">
            {step?.label}
          </h2>
          <span className="ml-auto text-xs text-gray-400">
            {currentStep + 1} / {steps.length}
          </span>
        </div>

        {/* ── Étape PDF simple ── */}
        {step?.type === 'pdf' && (
          <div className="space-y-4">
            <PdfViewer url={pdfUrl} label={step.label} />
            <CheckOption
              checked={acknowledged}
              onChange={() => setAcknowledged(v => !v)}
              label="J'ai pris connaissance de ce document et j'en accepte le contenu."
              color="blue"
            />
          </div>
        )}

        {/* ── Étape Attestation sur l'honneur ── */}
        {step?.type === 'attestation' && (
          <div className="space-y-4">
            <AttestationStep
              checks={attestationChecks}
              onCheck={i => {
                const next = [...attestationChecks];
                next[i] = !next[i];
                setAttestationChecks(next);
                setPreviewB64(null);
                setPreviewGenerated(false);
                setAcknowledged(false);
              }}
              villeSignature={villeSignature}
              onVilleChange={v => { setVilleSignature(v); setPreviewB64(null); setPreviewGenerated(false); }}
            />

            {allAttestationChecked && !previewGenerated && (
              <button
                onClick={() => handlePreview('attestation_honneur', { attestation_all_checked: true })}
                disabled={previewLoading}
                data-testid="preview-attestation-btn"
                className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-60"
              >
                {previewLoading ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
                {previewLoading ? 'Génération…' : 'Prévisualiser mon attestation signée'}
              </button>
            )}

            {previewGenerated && (
              <>
                <div className="rounded-xl overflow-hidden">
                  <div className="bg-indigo-100 px-4 py-2 flex items-center gap-2">
                    <CheckCircle size={14} className="text-indigo-600" />
                    <span className="text-xs font-medium text-indigo-700">Aperçu de votre attestation générée</span>
                  </div>
                  <PdfViewer b64={previewB64} loading={previewLoading} label="Attestation sur l'honneur" />
                </div>
                <CheckOption
                  checked={acknowledged}
                  onChange={() => setAcknowledged(v => !v)}
                  label="J'ai pris connaissance de l'attestation et confirme tous mes engagements."
                  color="indigo"
                />
              </>
            )}
          </div>
        )}

        {/* ── Étape Choix DFS ── */}
        {step?.type === 'choice_dfs' && (
          <div className="space-y-4">
            <DfsChoiceForm
              choices={dfsChoices}
              onChange={c => { setDfsChoices(c); setPreviewB64(null); setPreviewGenerated(false); setAcknowledged(false); }}
              villeSignature={villeSignature}
              onVilleChange={v => { setVilleSignature(v); setPreviewB64(null); setPreviewGenerated(false); }}
            />
            {!previewGenerated ? (
              <button
                onClick={() => handlePreview('dfs', dfsChoices)}
                disabled={previewLoading}
                data-testid="preview-dfs-btn"
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-60"
              >
                {previewLoading ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
                {previewLoading ? 'Génération du document…' : 'Générer et visualiser mon document DFS'}
              </button>
            ) : (
              <>
                <div className="rounded-xl overflow-hidden">
                  <div className="bg-blue-100 px-4 py-2 flex items-center gap-2">
                    <CheckCircle size={14} className="text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">Aperçu de votre DFS généré</span>
                  </div>
                  <PdfViewer b64={previewB64} loading={previewLoading} label="DFS — Choix annuel" />
                </div>
                <CheckOption
                  checked={acknowledged}
                  onChange={() => setAcknowledged(v => !v)}
                  label="J'ai pris connaissance de ce document DFS et confirme mon choix."
                  color="blue"
                />
              </>
            )}
          </div>
        )}

        {/* ── Étape Choix Mutuelle ── */}
        {step?.type === 'choice_mutuelle' && (
          <div className="space-y-4">
            <MutuelleChoiceForm
              choices={mutuelleChoices}
              onChange={c => { setMutuelleChoices(c); setPreviewB64(null); setPreviewGenerated(false); setAcknowledged(false); }}
              villeSignature={villeSignature}
              onVilleChange={v => { setVilleSignature(v); setPreviewB64(null); setPreviewGenerated(false); }}
            />
            {!previewGenerated ? (
              <button
                onClick={() => handlePreview('mutuelle', mutuelleChoices)}
                disabled={previewLoading || (mutuelleChoices.mutuelle_choix === 'dispense' && !mutuelleChoices.mutuelle_motif)}
                data-testid="preview-mutuelle-btn"
                className="w-full flex items-center justify-center gap-2 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition disabled:opacity-60"
              >
                {previewLoading ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
                {previewLoading ? 'Génération du document…' : 'Générer et visualiser mon document Mutuelle'}
              </button>
            ) : (
              <>
                <div className="rounded-xl overflow-hidden">
                  <div className="bg-teal-100 px-4 py-2 flex items-center gap-2">
                    <CheckCircle size={14} className="text-teal-600" />
                    <span className="text-xs font-medium text-teal-700">Aperçu de votre document Mutuelle généré</span>
                  </div>
                  <PdfViewer b64={previewB64} loading={previewLoading} label="Mutuelle — Affiliation/Dispense" />
                </div>
                <CheckOption
                  checked={acknowledged}
                  onChange={() => setAcknowledged(v => !v)}
                  label="J'ai pris connaissance de ce document et confirme mon choix Mutuelle."
                  color="teal"
                />
              </>
            )}
          </div>
        )}

        {/* ── Navigation ── */}
        <div className="flex items-center justify-between mt-6 gap-3">
          <button
            onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
            disabled={currentStep === 0}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition disabled:opacity-40"
          >
            <ChevronLeft size={16} /> Précédent
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed() || completing}
            data-testid="next-step-btn"
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {completing ? (
              <><Loader2 className="animate-spin" size={16} /> Finalisation…</>
            ) : isLast ? (
              <><CheckCircle size={16} /> Terminer la signature</>
            ) : (
              <>Suivant <ChevronRight size={16} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
